import { Box, Stack, Typography } from '@mui/material'
import { useRef, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useFormContext } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import {
  LabelMasterSaveForm,
  type LabelMasterFormData,
} from 'src/features/labelMaster/form/LabelMasterSaveForm'
import { useSaveLabelMaster } from '../api/useSaveLabelMaster'
import { EditHeader } from './EditHeader'
import { LabelAddButton, LabelCards } from './LabelSamples'
import { usePreventPageOut } from './usePreventPageOut'

type Props = {
  labelMasterId: string
}

export const LabelMasterEdit = ({ labelMasterId }: Props) => {
  const history = useHistory()
  const formRef = useRef<HTMLFormElement>(null)
  const { handleSubmit, formState, control } =
    useFormContext<LabelMasterFormData>()
  const updateMutation = useSaveLabelMaster('update')
  const [selectedLabelId, setSelectedLabelId] = useState<string | undefined>(
    undefined,
  )

  const { handleBeforeUnload } = usePreventPageOut({
    isModified: formState.isDirty,
    onLeavePage: () => history.goBack(),
  })

  const onSubmit = handleSubmit(data => {
    updateMutation.mutate(
      {
        id: labelMasterId,
        name: data.name,
        status: data.status,
        code: data.code,
        barcodeId: data.barcodeId || undefined,
      },
      {
        onSuccess: () => history.goBack(),
      },
    )
  })

  return (
    <Box bgcolor="background.grayLight" height="100vh" overflow="hidden">
      <EditHeader
        onBack={handleBeforeUnload}
        onSave={() => formRef.current?.requestSubmit()}
        canSave={!updateMutation.isPending}
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '480px 1fr',
          gap: 2,
          height: 'calc(100vh - 64px)',
        }}
      >
        <Box overflow="auto" bgcolor="background.paper" height="100%" p={2}>
          <form
            ref={formRef}
            onSubmit={e => {
              e.preventDefault()
              onSubmit()
            }}
          >
            <LabelMasterSaveForm control={control} />
          </form>
        </Box>
        <Box overflow="auto" height="100%" p={2}>
          <Stack gap={1}>
            <ErrorBoundary
              fallback={
                <Typography color="error">
                  ラベルの読み込みに失敗しました。
                </Typography>
              }
            >
              <LabelAddButton
                labelMasterId={labelMasterId}
                onSingleLabelAdded={setSelectedLabelId}
              />
              <LabelCards
                labelMasterId={labelMasterId}
                selectedLabelId={selectedLabelId}
                onSelectLabel={setSelectedLabelId}
                onDeselectLabel={() => setSelectedLabelId(undefined)}
              />
            </ErrorBoundary>
          </Stack>
        </Box>
      </Box>
    </Box>
  )
}
